<template>
    <template v-for="(filter, index) in filters" :key="filter.key">
        <template v-if="isShown(filter)">
            <hr v-if="previousShown(index) && (isLens(filter) || groupHeadingAt(index))" class="dropdown-divider" />

            <!-- The list's own fields come first, in its column order; the two runs
                 that follow are named rather than left looking like a sorting
                 accident. -->
            <b-dropdown-item v-if="groupHeadingAt(index)" custom>
                <span class="list-filter-group">{{ groupHeadingAt(index) }}</span>
            </b-dropdown-item>

            <!-- A short set of choices is the whole answer: show the choices, the
                 way this menu always has. Nothing to open, nothing to type. -->
            <template v-if="isLens(filter)">
                <b-dropdown-item custom>
                    <strong class="list-filter-lens">{{ filter.label }}</strong>
                </b-dropdown-item>
                <b-dropdown-item
                    v-for="option in lensOptions(filter)"
                    :key="filter.key + '-' + option.value"
                    @click="apply(filter, option.value)"
                >
                    <span class="list-filter-option">
                        <b-icon
                            icon="check"
                            size="is-small"
                            :class="{ 'is-invisible': valueOf(filter) !== option.value }"
                        />
                        {{ option.label }}
                    </span>
                </b-dropdown-item>
            </template>

            <!-- Everything else is a field name until you ask for it. Sixteen
                 filters as sixteen open inputs is a wall; the value of an active
                 one is on its row, so a folded menu never hides what is filtering. -->
            <b-dropdown-item v-else custom>
                <a class="list-filter-row" @click="toggle(filter)">
                    <span class="list-filter-name">{{ filter.label }}</span>
                    <span v-if="summaryOf(filter)" class="list-filter-summary">{{ summaryOf(filter) }}</span>
                    <b-icon :icon="isOpen(filter) ? 'chevron-down' : 'chevron-right'" size="is-small" />
                </a>

                <div v-if="isOpen(filter)" class="list-filter-field">
                    <!-- The control belongs to the filter's type, and is the same one
                         the search bar and the query builder use. -->
                    <filter-value-control
                        :qualifier="filter"
                        :operator="operatorOf(filter)"
                        :model-value="valueOf(filter)"
                        @update:model-value="value => onValueInput(filter, value)"
                    />

                    <a v-if="rawOf(filter)" class="list-filter-clear" @click="clear(filter)">
                        <b-icon icon="close" size="is-small" />
                        {{ $t('Clear') }}
                    </a>
                </div>
            </b-dropdown-item>
        </template>
    </template>

    <!-- The toggle stays in the DOM once shown: a menu item that removes itself
         on click is no longer a child of the menu when Buefy asks whether the
         click landed inside it, and the whole dropdown closes on the spot. -->
    <b-dropdown-item v-if="foldedCount > 0 || showAll" custom>
        <a class="list-filter-more" @click="showAll = !showAll">
            <b-icon :icon="showAll ? 'chevron-down' : 'chevron-right'" size="is-small" />
            <span v-if="showAll">{{ $t('Fewer filters') }}</span>
            <span v-else>{{ $t('More filters') }} ({{ foldedCount }})</span>
        </a>
    </b-dropdown-item>
</template>

<script setup>
import { computed, getCurrentInstance, ref, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import debounce from 'lodash/debounce';
import FilterValueControl from '@/components/base/FilterValueControl.vue';
import { useQualifierValues } from '@/composables/useQualifierValues';
import { parseFilterValue, serializeFilterValue, takesValue } from '@/utils/filterOperators';

/**
 * The "Filter by" menu of a list: one entry per filter the list declares, in the
 * order the organization gave its columns.
 *
 * The filters themselves come from the module's single search declaration (see
 * the `/search` skill), so a column, a custom field and a hand-written filter
 * all land here without this component knowing any of them by name. Values live
 * in the URL, exactly where the search bar's chips read and write them.
 *
 * The menu asks one thing per field and asks it one way: choosing *how* to
 * compare belongs to the search bar. This is the quick gesture.
 */
const props = defineProps({
    // Ordered qualifier descriptors: [{ key, label, type, enum_values, entity_type, ... }]
    filters: {
        type: Array,
        default: () => []
    }
});

// How many typed filters stand open-able before the rest folds away. The head of
// the list is what the org put first in its columns, so it is what people filter
// on; everything else is one click behind.
const HEAD_COUNT = 4;

// Beyond this, a set of choices is a list to scroll rather than a lens to glance
// at, and it folds like the others.
const LENS_MAX_OPTIONS = 4;

const { proxy } = getCurrentInstance();
const route = useRoute();
const router = useRouter();
const { labelFor, cachedLabel } = useQualifierValues();

// The field whose control is open, and whether the fold has been lifted.
const openKey = ref(null);
const showAll = ref(false);

/** The raw URL value, operator included. */
function rawOf(filter) {
    return route.query[filter.key] ?? '';
}

/** The value alone — what the control edits. */
function valueOf(filter) {
    return parseFilterValue(rawOf(filter), filter).value;
}

/**
 * How the URL currently compares — never chosen here.
 *
 * The operator belongs to the search bar. It is still read, so that a filter
 * written there ("ne contient pas Braun") keeps its meaning when the value is
 * edited from the menu instead of silently reverting to "contient".
 */
function operatorOf(filter) {
    return parseFilterValue(rawOf(filter), filter).operator;
}

/** An operator asking for no value ("est vide") leaves nothing to type. */
function needsValue(filter) {
    return takesValue(filter, operatorOf(filter));
}

/**
 * A filter whose whole answer is a handful of choices the module itself ships —
 * the birthday and marketing lenses this menu has always shown open.
 *
 * A custom field never qualifies, however few options it has: an organization
 * adds them by the dozen, and a dozen open option lists is the wall the fold
 * exists to prevent. Theirs is a select, inside their row.
 */
function isLens(filter) {
    return (
        groupOf(filter) !== 'custom' &&
        (filter.type === 'enum' || filter.type === 'boolean') &&
        (filter.enum_values?.length ?? 0) > 0 &&
        filter.enum_values.length <= LENS_MAX_OPTIONS
    );
}

/** "Tous" first, then the declared choices — the wording this menu always had. */
function lensOptions(filter) {
    return [
        { value: '', label: proxy.$t('All') },
        ...filter.enum_values.map(option => ({ value: String(option.value), label: option.label }))
    ];
}

/** Rank among the folding filters — the lenses are always shown, so they don't count. */
const foldRanks = computed(() => {
    const ranks = {};
    let rank = 0;

    for (const filter of props.filters) {
        ranks[filter.key] = isLens(filter) ? -1 : rank++;
    }

    return ranks;
});

/**
 * An active filter is never folded away: a list filtering on something the menu
 * does not show is how people end up believing a record disappeared.
 */
function isShown(filter) {
    return showAll.value || isLens(filter) || rawOf(filter) !== '' || foldRanks.value[filter.key] < HEAD_COUNT;
}

const foldedCount = computed(() => props.filters.filter(filter => !isShown(filter)).length);

function isOpen(filter) {
    return openKey.value === filter.key;
}

/** One control open at a time — the menu stays short while being used. */
function toggle(filter) {
    openKey.value = isOpen(filter) ? null : filter.key;
}

/** What an active filter reads as on its own row. */
function summaryOf(filter) {
    const value = valueOf(filter);

    if (rawOf(filter) === '') {
        return '';
    }

    if (!needsValue(filter)) {
        return proxy.$t('Empty');
    }

    if (filter.type === 'relation') {
        return cachedLabel(filter, value) || value;
    }

    if (filter.type === 'enum' || filter.type === 'boolean') {
        const match = (filter.enum_values || []).find(option => String(option.value) === value);

        return match?.label ?? value;
    }

    return value;
}

/** The nearest filter above this one that the menu is currently showing. */
function previousShown(index) {
    for (let i = index - 1; i >= 0; i--) {
        if (isShown(props.filters[i])) {
            return props.filters[i];
        }
    }

    return null;
}

// The three runs the descriptor returns, in order.
function groupOf(filter) {
    if (filter.key?.startsWith('cf_')) {
        return 'custom';
    }

    return filter.column ? 'column' : 'other';
}

/**
 * The heading opening a run, or null mid-run. The backend groups the filters
 * already; this only names the boundaries.
 *
 * The boundary is measured against the previous *shown* filter: a folded
 * neighbour would otherwise swallow the heading of the run it belongs to.
 */
function groupHeadingAt(index) {
    const group = groupOf(props.filters[index]);
    const previous = previousShown(index);

    if (previous && groupOf(previous) === group) {
        return null;
    }

    if (group === 'other') {
        return proxy.$t('Other filters');
    }

    return group === 'custom' ? proxy.$t('Custom fields') : null;
}

/**
 * Write the filter to the URL, keeping whatever operator it already carried.
 */
function apply(filter, value) {
    const query = { ...route.query };
    const serialized = serializeFilterValue(operatorOf(filter), value, filter);

    if (serialized === '') {
        delete query[filter.key];
    } else {
        query[filter.key] = serialized;
    }

    router.push({ name: route.name, params: route.params, query });
}

// Typing filters as you type would push a route per keystroke.
const applyDebounced = debounce((filter, value) => apply(filter, value), 400);

/**
 * A value that is typed waits for the typing to stop; one that is *picked* — an
 * option, a record, a day — is a finished gesture and applies at once.
 */
function onValueInput(filter, value) {
    if (filter.type === 'text' || filter.type === 'number') {
        applyDebounced(filter, value);

        return;
    }

    apply(filter, value);
}

function clear(filter) {
    apply(filter, '');
}

/**
 * Render the name behind an id already in the URL, so an active filter does not
 * read as "1234" on its row. The control resolves its own label; this is for the
 * rows that stay folded.
 */
async function labelActiveRelations() {
    for (const filter of props.filters) {
        if (filter.type !== 'relation') {
            continue;
        }

        const value = valueOf(filter);

        if (value) {
            await labelFor(filter, value);
        }
    }
}

watch([() => props.filters, () => route.query], labelActiveRelations, { immediate: true, deep: true });
</script>

<style scoped>
.list-filter-field {
    min-width: 15rem;
    padding: 0.25rem 0 0.15rem;
}
.list-filter-row {
    display: flex;
    align-items: center;
    gap: 0.4rem;
    min-width: 15rem;
    color: inherit;
}
.list-filter-name {
    flex: 1 1 auto;
}
.list-filter-summary {
    max-width: 8rem;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    font-size: 0.75rem;
    color: var(--bulma-text-weak);
}
.list-filter-lens {
    font-size: 0.85rem;
}
.list-filter-option {
    display: flex;
    align-items: center;
    gap: 0.35rem;
}
.list-filter-more {
    display: flex;
    align-items: center;
    gap: 0.25rem;
    font-size: 0.8rem;
    color: var(--bulma-text-weak);
}
.list-filter-clear {
    display: inline-flex;
    align-items: center;
    gap: 0.2rem;
    margin-top: 0.25rem;
    font-size: 0.75rem;
    color: var(--bulma-text-weak);
}
.list-filter-group {
    font-size: 0.7rem;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    color: var(--bulma-text-weak);
}
</style>
